import { HttpClient, HttpHeaders, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AlertService } from '../../shared/alert/shared/alert.service';
import { AlertTypes } from '../../shared/alert/shared/alert-types';
import { ParamsType } from './api-base-actions';
import { AuthTokenService } from './auth-token.service';
import { HttpContentTypes } from './http-content-types.enum';

interface ApiErrorPayload {
  status?: number;
  code?: string;
  title?: string;
  message: string;
  raw?: any;
}

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  public apiAddress: string = environment.apiAddress;

  constructor(
    private readonly http: HttpClient,
    private readonly authTokenService: AuthTokenService,
    private readonly alertService: AlertService
  ) {
  }

  public get<T>(url: string, isJwtTokenRequired: boolean = true): Observable<T> {
    return this.requestWithAuth<T>(
      headers => this.http.get<T>(this.apiAddress + url, { headers }),
      isJwtTokenRequired,
      HttpContentTypes.json
    );
  }

  public post<T>(
    url: string,
    data: any,
    isJwtTokenRequired: boolean = true,
    contentType: HttpContentTypes = HttpContentTypes.json
  ): Observable<T> {
    return this.requestWithAuth<T>(
      headers => this.http.post<T>(this.apiAddress + url, data, { headers }),
      isJwtTokenRequired,
      contentType
    );
  }

  public put<T>(url: string, data: any, isJwtTokenRequired: boolean = true): Observable<T> {
    return this.requestWithAuth<T>(
      headers => this.http.put<T>(this.apiAddress + url, data, { headers }),
      isJwtTokenRequired,
      HttpContentTypes.json
    );
  }

  public patch<T>(url: string, data: any, isJwtTokenRequired: boolean = true): Observable<T> {
    return this.requestWithAuth<T>(
      headers => this.http.patch<T>(this.apiAddress + url, data, { headers }),
      isJwtTokenRequired,
      HttpContentTypes.json
    );
  }

  public head<T>(url: string, isJwtTokenRequired: boolean = true): Observable<T> {
    return this.requestWithAuth<T>(
      headers => this.http.head<T>(this.apiAddress + url, { headers }),
      isJwtTokenRequired,
      HttpContentTypes.json
    );
  }

  public delete<T>(url: string, isJwtTokenRequired: boolean = true): Observable<T> {
    return this.requestWithAuth<T>(
      headers => this.http.delete<T>(this.apiAddress + url, { headers }),
      isJwtTokenRequired,
      HttpContentTypes.json
    );
  }

  public getBlob(url: string, params?: ParamsType): Observable<Blob> {
    return this.requestWithAuth<Blob>(
      headers => this.http.get(this.apiAddress + url, {
        headers,
        responseType: 'blob',
        params: this.createParams(params)
      }),
      true
    );
  }

  private getFirstValidationErrorMessage(error: any): string | null {
    const validationErrors = error?.errors;

    if (!validationErrors || typeof validationErrors !== 'object') {
      return null;
    }

    for (const messages of Object.values(validationErrors)) {
      if (Array.isArray(messages)) {
        const firstMessage = messages.find(message => typeof message === 'string' && message.trim().length > 0);
        if (firstMessage) {
          return firstMessage;
        }
      }
    }

    return null;
  }

  /* Downloads */
  public postBlob(url: string, body: any, params?: ParamsType): Observable<Blob> {
    return this.requestWithAuth<Blob>(
      headers => this.http.post(this.apiAddress + url, body, {
        headers,
        responseType: 'blob',
        params: this.createParams(params)
      }),
      true
    );
  }

  /* Stream */
  public getStream(url: string, params?: ParamsType): Observable<Blob> {
    return this.requestWithAuth<Blob>(
      headers => this.http.get(this.apiAddress + url, {
        headers,
        responseType: 'blob',
        params: this.createParams(params)
      }),
      true
    );
  }

  // Post without jwtToken in header, but withCredentials means here add cookies/refresh token yes or no
  // Can be used for anonymous calls, currently User/authenticate, User/logintwostep, User/complete-required-two-factor-setup
  // Post without JWT header. withCredentials sends/receives the refresh-token cookie. Errors are passed
  // through untouched by default because login/2FA show their own messages; mapErrors = true runs them
  // through the same alert mapping as post() (e.g. the password policy toast for reset-password).
  public postWithCredentials<T>(endpoint: string, data: any, withCredentials: boolean = true, mapErrors: boolean = false): Observable<T> {
    const request$ = this.http.post<T>(
      `${this.apiAddress}${endpoint}`,
      data,
      { withCredentials }
    );
    return mapErrors ? request$.pipe(catchError(error => this.handleHttpError<T>(error))) : request$;
  }

  // Get without jwtToken in header, but withCredentials means here add cookies/refresh token yes or no
  // Can be used for anonymous calls, currently used with cookies for User/refresh-token
  public getWithCredentials<T>(endpoint: string, withCredentials: boolean = true): Observable<T> {
    return this.http.get<T>(
      `${this.apiAddress}${endpoint}`,
      { withCredentials }
    );
  }

  // public putWithCredentials<T>(endpoint: string, data: any, withCredentials: boolean = true): Observable<T> {
  //   return this.http.put<T>(
  //     `${this.apiAddress}${endpoint}`,
  //     data,
  //     { withCredentials }
  //   );
  // }

  // public deleteWithCredentials<T>(endpoint: string, withCredentials: boolean = true): Observable<T> {
  //   return this.http.delete<T>(
  //     `${this.apiAddress}${endpoint}`,
  //     { withCredentials }
  //   );
  // }

  private requestWithAuth<T>(
    requestFactory: (headers: HttpHeaders) => Observable<T>,
    isJwtTokenRequired: boolean,
    contentType?: HttpContentTypes
  ): Observable<T> {
    if (!isJwtTokenRequired) {
      return requestFactory(this.createHeaders(null, false, contentType)).pipe(
        catchError(error => this.handleHttpError<T>(error))
      );
    }

    return this.authTokenService.getValidAccessToken().pipe(
      switchMap(token => this.executeRequest(requestFactory, token, contentType)),
      catchError(error => this.handleTokenError<T>(error))
    );
  }

  private executeRequest<T>(
    requestFactory: (headers: HttpHeaders) => Observable<T>,
    token: string,
    contentType?: HttpContentTypes
  ): Observable<T> {
    return requestFactory(this.createHeaders(token, true, contentType)).pipe(
      catchError(error => {
        if (!this.isUnauthorized(error)) {
          return this.handleHttpError<T>(error);
        }

        return this.retryRequestAfterRefresh(requestFactory, contentType);
      })
    );
  }

  private retryRequestAfterRefresh<T>(
    requestFactory: (headers: HttpHeaders) => Observable<T>,
    contentType?: HttpContentTypes
  ): Observable<T> {
    return this.authTokenService.getValidAccessToken(true).pipe(
      switchMap(token => requestFactory(this.createHeaders(token, true, contentType)).pipe(
        catchError(error => {
          if (this.isUnauthorized(error)) {
            this.authTokenService.logout('unauthorized');
          }

          return this.handleHttpError<T>(error);
        })
      )),
      catchError(error => this.handleTokenError<T>(error))
    );
  }

  private handleTokenError<T>(error: any): Observable<T> {
    if (this.isApiErrorPayload(error)) {
      return throwError(() => error);
    }

    if (this.isHttpErrorResponse(error)) {
      return this.handleHttpError<T>(error);
    }

    return throwError(() => error);
  }

  private createHeaders(
    token: string | null,
    isJwtTokenRequired: boolean,
    contentType?: HttpContentTypes
  ): HttpHeaders {
    let headers = new HttpHeaders();

    if (contentType === HttpContentTypes.json) {
      headers = headers.set('Content-Type', 'application/json');
    }

    if (isJwtTokenRequired && token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  private createParams(params?: ParamsType): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        httpParams = httpParams.append(key, value);
      });
    }
    return httpParams;
  }

  private handleHttpError<T>(resp: any): Observable<T> {
    return throwError(() => this.getApiResponseError(resp));
  }

  private getApiResponseError(resp: any): ApiErrorPayload {
    const rawError = resp?.error;
    const errorCode = rawError?.errorCode ?? rawError?.code;
    const title = typeof rawError?.title === 'string' ? rawError.title : undefined;
    const message = (typeof rawError?.message === 'string' ? rawError.message : undefined);
  
     // Special case as bugfix for non expected api response in just this case
    if (title === 'Not Found' || rawError?.message === 'No RouteStation found') {
      const obj : ApiErrorPayload = {
        status: HttpStatusCode.Ok,
        code: undefined,
        title: "",
        message: "",
        raw: resp
      };
      return(obj);
    }

    var alertMessage = 'Unbekannter Fehlerstatus: '+resp?.status;
    var duration =  2000;
    var messageFallback = 'Unbekannter Fehlerstatus: '+resp?.status;

    switch (resp?.status) {
      case 401:
        alertMessage = 'Ihre Sitzung ist abgelaufen';
        messageFallback = 'Unauthorized | Sitzung abgelaufen';
        break;
      case 404:
        alertMessage = 'Die angeforderte Ressource wurde nicht gefunden';
        messageFallback = 'Not Found | Die angeforderte Ressource wurde nicht gefunden';
        break;
      case 403:
        alertMessage = 'Sie haben keine Berechtigung für diese Aktion.';
        messageFallback = 'Forbidden | Keine Berechtigung';
        break;
      case 409:
        alertMessage = 'Es ist ein Konflikt aufgetreten.';
        messageFallback = 'Conflict | Widerspruch';
        break;
      case 422: {
        alertMessage = 'Die Anfrage konnte nicht verarbeitet werden.';
        // Special case for password errors: Determine by error code and set fix german title by now
        if (typeof errorCode === 'string' && errorCode.toLowerCase() === 'passwordpolicyviolation') {
          alertMessage = 'Das Passwort entspricht nicht den Richtlinien: Länge 15 bis 64 Zeichen, mindestens je ein Groß- und Kleinbuchstabe, eine Ziffer und ein Sonderzeichen @ # $ % ^ & - _ + = [ ] { } | : , . ? / ~ ( ) ; !';
          duration = 10000;
        }
        if (typeof errorCode === 'string' && errorCode.toLowerCase() === 'passwordupdateviolation') {
          alertMessage = 'Das neue Passwort darf nicht dem alten entsprechen';
          duration = 10000;
        }
        if (typeof errorCode === 'string' && errorCode.toLowerCase() === 'passwordproofviolation') {
          alertMessage = 'Das angegebene Passwort ist ungültig';
          duration = 10000;
        }
        messageFallback = 'Unprocessable Entity |' +alertMessage;
        break;
      }
      case 429: {
        duration = 10000;
        alertMessage = 'Ihr Konto wurde vorübergehend gesperrt.';
        messageFallback = 'Too many attempts | Sperrung wegen Fehlversuchen';
        break;
      }
      case 500:
        alertMessage = 'Interner Server Fehler';
        messageFallback = 'Internal Server Error | Code 500';
        break;

      case undefined:
        alertMessage = 'Fehlerstatus undefiniert';
        messageFallback = 'Undefined | Error status undefined';
        break;

      }
    
      this.alertService.alert(alertMessage, AlertTypes.error, duration);

      const apiError: ApiErrorPayload = {
        status: resp?.status,
        code: errorCode ? String(errorCode) : undefined,
        title,
        message: message ?? messageFallback,
        raw: resp
      };

      var resolvedTitle = title ?? message ?? messageFallback;
      if (errorCode) {
        resolvedTitle =  `[${errorCode}] ${resolvedTitle}`;
      }
      return { ...apiError, message: resolvedTitle };
  }

  private isUnauthorized(error: any): boolean {
    return this.isHttpErrorResponse(error) && error.status === 401;
  }

  private isHttpErrorResponse(error: any): boolean {
    return !!error && typeof error.status === 'number';
  }

  private isApiErrorPayload(error: any): error is ApiErrorPayload {
    return !!error &&
      typeof error === 'object' &&
      typeof error.message === 'string' &&
      Object.prototype.hasOwnProperty.call(error, 'raw');
  }
}
